/**
 * Frontend form type matching PPI_Registry_Smartsheet / sign-up form.
 * Used by both SignUp and Profile (edit registration) pages.
 */
export type SignUpInput = {
    firstName: string;
    middleName?: string;
    lastName: string;
    suffix?: string;
    email: string;
    phone: string;
    businessName?: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    postalCode: string;
    licenseType: string;
    licenseNumber?: string;
    dateOfBirth?: string;
    countiesOfferingServices: string;
    contactInformationPrivate: string;
    cehpNumber?: string;
    cehpExpirationDate?: string;
    engineerNumber?: string;
    engineerExpirationDate?: string;
    mstcNumber?: string;
    mstcExpiration?: string;
};

/** API response from GET RegistryProfile/GetByEmail (camelCase from backend). */
export interface RegistryProfileApiResponse {
    registryID: number;
    firstName: string;
    middleName?: string | null;
    lastName: string;
    suffix?: string | null;
    email: string;
    phone: string;
    businessName?: string | null;
    streetAddressLine1: string;
    streetAddressLine2?: string | null;
    city: string;
    state: string;
    postalCode: string;
    licenseType: string;
    licenseNumber?: string | null;
    dateOfBirth?: string | null;
    countiesOfferingServices: string;
    contactInformationPrivate: boolean;
    qualificationExpiryDate?: string | null;
}

/** Empty/default SignUpInput for pre-fill. */
const EMPTY_SIGN_UP_INPUT: SignUpInput = {
    firstName: "",
    middleName: "",
    lastName: "",
    suffix: "",
    email: "",
    phone: "",
    businessName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    postalCode: "",
    licenseType: "",
    licenseNumber: "",
    dateOfBirth: "",
    countiesOfferingServices: "",
    contactInformationPrivate: "",
    cehpNumber: "",
    cehpExpirationDate: "",
    engineerNumber: "",
    engineerExpirationDate: "",
    mstcNumber: "",
    mstcExpiration: ""
};

/**
 * Read a string from profile using either camelCase or PascalCase key (API may serialize either way).
 */
function getStr(profile: Record<string, unknown>, camelKey: string): string {
    const pascalKey = camelKey.charAt(0).toUpperCase() + camelKey.slice(1);
    const raw = (profile[camelKey] ?? profile[pascalKey]) as string | null | undefined;
    return typeof raw === "string" ? raw.trim() : "";
}

/**
 * Build partial SignUpInput from the same user/profile object the header uses (e.g. useProfile / GetUserSecurityByEmail).
 * Accepts both camelCase and PascalCase so it works regardless of API JSON serialization.
 * Maps name, email, phone, address, city, zipCode so user details prepopulate.
 */
export function getSignUpInitialDataFromProfile(profile: {
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    middleName?: string | null;
    phone?: string | null;
    address?: string | null;
    city?: string | null;
    zipCode?: string | null;
    state?: string | null;
} | Record<string, unknown>): SignUpInput {
    const data: SignUpInput = { ...EMPTY_SIGN_UP_INPUT };
    const p = profile as Record<string, unknown>;
    const email = getStr(p, "email");
    if (email) data.email = email;
    const firstName = getStr(p, "firstName");
    if (firstName) data.firstName = firstName;
    const lastName = getStr(p, "lastName");
    if (lastName) data.lastName = lastName;
    const middleName = getStr(p, "middleName");
    if (middleName) data.middleName = middleName;
    const phone = getStr(p, "phone");
    if (phone) data.phone = phone;
    const address = getStr(p, "address");
    if (address) data.address1 = address;
    const city = getStr(p, "city");
    if (city) data.city = city;
    const zipCode = getStr(p, "zipCode");
    if (zipCode) data.postalCode = zipCode;
    const state = getStr(p, "state");
    if (state) data.state = state;
    return data;
}

/**
 * Build partial SignUpInput from MSAL account (e.g. when no registration found).
 * Prefills email and, if available, first/last name from display name.
 */
export function getSignUpInitialDataFromMsal(email: string, displayName?: string | null): SignUpInput {
    const data: SignUpInput = { ...EMPTY_SIGN_UP_INPUT };
    if (email?.trim()) data.email = email.trim();
    if (displayName?.trim()) {
        const parts = displayName.trim().split(/\s+/);
        if (parts.length === 1) {
            data.firstName = parts[0];
        } else {
            data.firstName = parts[0];
            data.lastName = parts.slice(1).join(" ");
        }
    }
    return data;
}

/** Map API profile response to SignUpInput form shape. Dates may be ISO strings. */
export function mapProfileResponseToSignUpInput(r: RegistryProfileApiResponse): SignUpInput {
    const expiry = r.qualificationExpiryDate != null ? String(r.qualificationExpiryDate).slice(0, 10) : '';
    const dob = r.dateOfBirth != null ? String(r.dateOfBirth).slice(0, 10) : '';
    return {
        firstName: r.firstName ?? '',
        middleName: r.middleName ?? '',
        lastName: r.lastName ?? '',
        suffix: r.suffix ?? '',
        email: r.email ?? '',
        phone: r.phone ?? '',
        businessName: r.businessName ?? '',
        address1: r.streetAddressLine1 ?? '',
        address2: r.streetAddressLine2 ?? '',
        city: r.city ?? '',
        state: r.state ?? '',
        postalCode: r.postalCode ?? '',
        licenseType: r.licenseType ?? '',
        licenseNumber: r.licenseNumber ?? '',
        dateOfBirth: dob,
        countiesOfferingServices: r.countiesOfferingServices ?? '',
        contactInformationPrivate: r.contactInformationPrivate ? 'Yes' : 'No',
        cehpNumber: r.licenseType === 'Certified Environmental Health Professional' ? (r.licenseNumber ?? '') : '',
        cehpExpirationDate: r.licenseType === 'Certified Environmental Health Professional' ? expiry : '',
        engineerNumber: (r.licenseType === 'Engineer who has completed the ACT' || r.licenseType === 'Person supervised by an engineer') ? (r.licenseNumber ?? '') : '',
        engineerExpirationDate: (r.licenseType === 'Engineer who has completed the ACT' || r.licenseType === 'Person supervised by an engineer') ? expiry : '',
        mstcNumber: r.licenseType === 'Master Septic Tank Contractor' ? (r.licenseNumber ?? '') : '',
        mstcExpiration: r.licenseType === 'Master Septic Tank Contractor' ? expiry : ''
    };
}
